import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { buildFunctionUrl } from '../../config/functions';
import accessoryCatalog from '../../config/accessories';
import {
  colorOptions,
  finishOptions,
  flattenDesignCatalog,
  glassOptions,
  productCategories,
} from '../../config/productCatalog';
import { getWindowBasePrice } from '../../config/windowPricing';
import html2pdf from 'html2pdf.js';

const SellerProposals = () => {
  const auth = getAuth();
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderSubmitting, setBuilderSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => auth.currentUser);

  const emptyItem = { name: '', description: '', quantity: 1, price: '' };
  const EMAIL_ENDPOINT = 'https://sendclientemail-xjpm53xzha-uc.a.run.app/sendClientEmail';
  const [itemList, setItemList] = useState([emptyItem]);
  const [deposit, setDeposit] = useState('');
  const [clientForm, setClientForm] = useState({
    clientId: '',
    name: '',
    email: '',
    phone: '',
    projectLocation: '',
  });
  const [builderError, setBuilderError] = useState('');
  const designCatalog = useMemo(() => flattenDesignCatalog(), []);
  const accessoryOptions = accessoryCatalog;
  const defaultCategory = productCategories[0]?.id || 'ventanas';
  const defaultProduct =
    designCatalog.find((design) => design.categoryId === defaultCategory)?.id ||
    designCatalog[0]?.id ||
    '';
  const [catalogBuilder, setCatalogBuilder] = useState({
    categoryId: defaultCategory,
    productId: defaultProduct,
    width: '',
    height: '',
    series: 'lama3',
    quantity: 1,
    color: colorOptions[0]?.id || '',
    glass: glassOptions[0]?.id || '',
    finish: finishOptions[0]?.id || '',
  });
  const [addonSelection, setAddonSelection] = useState({});
  const [proposalEmailId, setProposalEmailId] = useState('');
  const [proposalPdfId, setProposalPdfId] = useState('');

  const buildProposalHtml = (proposal = {}) => {
    const client = proposal.clientInfo || {};
    const issueDate = new Date(proposal.createdAt?.toDate?.() || Date.now()).toLocaleDateString('es-PR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const itemsHtml = (proposal.items || [])
      .map((item) => {
        const quantity = Number(item.quantity || 1);
        const price = Number(item.price || 0);
        return `
          <tr>
            <td style="padding:8px; border:1px solid #e5e7eb;">${item.name || '-'}</td>
            <td style="padding:8px; border:1px solid #e5e7eb;">${item.description || '-'}</td>
            <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">${quantity}</td>
            <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">$${price.toFixed(2)}</td>
            <td style="padding:8px; border:1px solid #e5e7eb; text-align:right;">$${(price * quantity).toFixed(2)}</td>
          </tr>`;
      })
      .join('');
    const total = Number(proposal.total || 0);
    const depositValue = Number(proposal.deposit || client.deposit || 0);
    const remaining = Number(proposal.remaining || total - depositValue);
    return `
      <div style="font-family:'Helvetica Neue',Arial,sans-serif;padding:16px;color:#111827;">
        <div style="text-align:center;border-bottom:2px solid #047857;padding-bottom:10px;margin-bottom:16px;">
          <h2 style="margin:0;color:#047857;">Puerto Rico Windows & Doors</h2>
          <p style="margin:4px 0 0;font-size:13px;">Propuesta preparada por ${proposal.createdBy?.name || 'Equipo de ventas'}</p>
        </div>
        <p><strong>Fecha:</strong> ${issueDate}</p>
        <p><strong>Cliente:</strong> ${client.name || 'N/A'}</p>
        <p><strong>Email:</strong> ${client.email || 'N/A'}</p>
        <p><strong>Proyecto:</strong> ${client.projectLocation || '—'}</p>
        <table style="width:100%;border-collapse:collapse;margin-top:12px;font-size:13px;">
          <thead>
            <tr style="background:#ecfdf5;color:#065f46;">
              <th style="padding:8px;border:1px solid #e5e7eb;">Modelo</th>
              <th style="padding:8px;border:1px solid #e5e7eb;">Descripción</th>
              <th style="padding:8px;border:1px solid #e5e7eb;">Cantidad</th>
              <th style="padding:8px;border:1px solid #e5e7eb;">Precio</th>
              <th style="padding:8px;border:1px solid #e5e7eb;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml || '<tr><td colspan="5" style="padding:8px;text-align:center;">Sin partidas</td></tr>'}
            <tr><td colspan="4" style="padding:8px;text-align:right;">Total</td><td style="padding:8px;text-align:right;">$${total.toFixed(
              2
            )}</td></tr>
            <tr><td colspan="4" style="padding:8px;text-align:right;">Depósito sugerido</td><td style="padding:8px;text-align:right;">$${depositValue.toFixed(
              2
            )}</td></tr>
            <tr><td colspan="4" style="padding:8px;text-align:right;font-weight:bold;">Saldo estimado</td><td style="padding:8px;text-align:right;font-weight:bold;">$${Math.max(
              remaining,
              0
            ).toFixed(2)}</td></tr>
          </tbody>
        </table>
        <p style="margin-top:16px;font-size:13px;">
          *Montos preliminares sujetos a verificación de medidas, disponibilidad y certificaciones requeridas.
        </p>
      </div>
    `;
  };

  const generateProposalPdfBlob = (proposal) =>
    html2pdf()
      .from(buildProposalHtml(proposal))
      .set({
        margin: 0.5,
        filename: `Propuesta-${proposal.id || proposal.publicToken || 'PRWD'}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      })
      .outputPdf('blob');

  const downloadProposalPdf = (proposal) =>
    html2pdf()
      .from(buildProposalHtml(proposal))
      .set({
        margin: 0.5,
        filename: `Propuesta-${proposal.id || proposal.publicToken || 'PRWD'}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      })
      .save();

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        try {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const sendProposalEmailToClient = async (proposal, { silent = false } = {}) => {
    const email = proposal?.clientInfo?.email;
    if (!email) {
      if (!silent) alert('Este cliente no tiene email registrado.');
      return;
    }
    try {
      const blob = await generateProposalPdfBlob(proposal);
      const base64Pdf = await blobToBase64(blob);
      const response = await fetch(EMAIL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          subject: `Propuesta Puerto Rico Windows & Doors · ${proposal.id || proposal.publicToken || ''}`,
          text: 'Adjuntamos tu propuesta preliminar. Confirma si deseas coordinar visita técnica.',
          html: '<p>Adjunto encontrarás la propuesta solicitada.</p>',
          attachments: [
            {
              content: base64Pdf,
              filename: `Propuesta-${proposal.id || proposal.publicToken || 'PRWD'}.pdf`,
              type: 'application/pdf',
              disposition: 'attachment',
            },
          ],
        }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No pudimos enviar el correo.');
      }
      if (!silent) alert('Propuesta enviada al cliente.');
    } catch (err) {
      console.error('❌ Error enviando propuesta:', err);
      if (!silent) alert(err.message || 'No pudimos enviar la propuesta.');
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => setCurrentUser(user));
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const fetchProposals = async () => {
      const email = auth.currentUser?.email?.toLowerCase();
      const uid = auth.currentUser?.uid || null;
      if (!email && !uid) return;
      setLoading(true);
      setError(null);
      try {
        const ref = collection(db, 'proposalTokens');
        const queries = [];
        if (email) {
          queries.push(query(ref, where('createdBy.email', '==', email)));
        }
        if (uid) {
          queries.push(query(ref, where('createdBy.uid', '==', uid)));
        }
        const snapshots = await Promise.all(queries.map((q) => getDocs(q)));
        const map = new Map();
        snapshots.forEach((snap) => {
          snap.forEach((docSnap) => {
            map.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
          });
        });
        const data = Array.from(map.values());
        data.sort(
          (a, b) =>
            (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
        );
        setProposals(data);
      } catch (err) {
        console.error('❌ Error listando propuestas del vendedor:', err);
        setError('No pudimos cargar tus propuestas.');
      } finally {
        setLoading(false);
      }
    };

    fetchProposals();
  }, [auth]);

  useEffect(() => {
    const fetchClients = async () => {
      const uid = auth.currentUser?.uid || null;
      const email = auth.currentUser?.email?.toLowerCase();
      if (!uid && !email) return;
      try {
        const snap = await getDocs(collection(db, 'clients'));
        const allowed = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((client) => {
            const ownerUid = client.ownerUid || client.createdByUid || null;
            const assignedUid =
              client.assignedSellerUid ||
              client.assignedToUid ||
              client.salesRepUid ||
              null;
            const assignedEmail =
              (client.assignedSellerEmail || client.assignedToEmail || '').toLowerCase();
            return (
              ownerUid === uid ||
              assignedUid === uid ||
              (assignedEmail && email && assignedEmail === email)
            );
          });
        allowed.sort((a, b) => (a.businessName || '').localeCompare(b.businessName || ''));
        setClients(allowed);
      } catch (err) {
        console.warn('No pudimos cargar clientes para propuestas del vendedor:', err);
      }
    };
    fetchClients();
  }, [auth]);

  const resetBuilder = () => {
    setClientForm({
      clientId: '',
      name: '',
      email: '',
      phone: '',
      projectLocation: '',
    });
    setItemList([emptyItem]);
    setDeposit('');
    setBuilderError('');
    setCatalogBuilder((prev) => ({
      ...prev,
      categoryId: defaultCategory,
      productId: defaultProduct,
      width: '',
      height: '',
      series: 'lama3',
      quantity: 1,
      color: colorOptions[0]?.id || '',
      glass: glassOptions[0]?.id || '',
      finish: finishOptions[0]?.id || '',
    }));
    setAddonSelection({});
  };

  const handleSelectClient = (clientId) => {
    setClientForm((prev) => ({ ...prev, clientId }));
    if (!clientId) return;
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setClientForm({
        clientId,
        name: client.businessName || client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        projectLocation: client.projectLocation || client.address || '',
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...itemList];
    updated[index][field] = field === 'quantity' || field === 'price' ? value.replace(',', '.') : value;
    setItemList(updated);
  };

  const addItem = () => setItemList((prev) => [...prev, { ...emptyItem }]);
  const removeItem = (index) =>
    setItemList((prev) => prev.filter((_, i) => i !== index || prev.length === 1));

  const itemsPrepared = itemList
    .map((item) => ({
      name: item.name?.trim(),
      description: item.description?.trim() || '',
      quantity: Number(item.quantity) || 1,
      price: Number(item.price) || 0,
    }))
    .filter((item) => item.name);

  const total = itemsPrepared.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const depositNumber = Number(deposit) || 0;

  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    setBuilderError('');
    if (!clientForm.name.trim() || !clientForm.email.trim()) {
      setBuilderError('Nombre y email del cliente son obligatorios.');
      return;
    }
    if (itemsPrepared.length === 0) {
      setBuilderError('Agrega al menos una línea con nombre y precio.');
      return;
    }
    if (!currentUser?.email) {
      setBuilderError('Tu sesión venció, vuelve a iniciar sesión.');
      return;
    }

    const clientInfo = {
      id: clientForm.clientId || null,
      name: clientForm.name.trim(),
      email: clientForm.email.trim().toLowerCase(),
      phone: clientForm.phone.trim() || '',
      projectLocation: clientForm.projectLocation.trim() || '',
      deposit: depositNumber,
    };

    const payload = {
      clientInfo,
      items: itemsPrepared,
      total,
      deposit: depositNumber,
      remaining: Math.max(total - depositNumber, 0),
      createdBy: {
        uid: currentUser.uid,
        email: currentUser.email.toLowerCase(),
        role: 'seller',
        name: currentUser.displayName || clientForm.name || '',
      },
      source: 'seller-panel',
    };

    setBuilderSubmitting(true);
    try {
      const res = await fetch(buildFunctionUrl('/sendProjectProposal'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result.error || 'No pudimos enviar la propuesta.');
      }
      setBuilderOpen(false);
      resetBuilder();
      setProposals((prev) => [
        {
          id: result.proposalId || `local-${Date.now()}`,
          ...payload,
          status: 'pendiente',
          createdAt: { seconds: Date.now() / 1000 },
        },
        ...prev,
      ]);
    } catch (err) {
      console.error('❌ Error enviando propuesta desde vendedor:', err);
      setBuilderError(err.message || 'No pudimos enviar la propuesta.');
    } finally {
      setBuilderSubmitting(false);
    }
  };

  const availableDesigns = useMemo(
    () => designCatalog.filter((design) => design.categoryId === catalogBuilder.categoryId),
    [designCatalog, catalogBuilder.categoryId]
  );

  useEffect(() => {
    if (!catalogBuilder.productId && availableDesigns.length) {
      setCatalogBuilder((prev) => ({ ...prev, productId: availableDesigns[0].id }));
    }
  }, [availableDesigns, catalogBuilder.productId]);

  const catalogPricePreview = useMemo(() => {
    const width = Number(catalogBuilder.width);
    const height = Number(catalogBuilder.height);
    if (!width || !height) return null;
    return getWindowBasePrice(width, height, catalogBuilder.series);
  }, [catalogBuilder.width, catalogBuilder.height, catalogBuilder.series]);

  const filteredAccessories = useMemo(() => {
    return accessoryOptions.filter(
      (addon) =>
        !addon.appliesTo?.length ||
        addon.appliesTo.includes(catalogBuilder.categoryId) ||
        addon.appliesTo.includes('todos')
    );
  }, [accessoryOptions, catalogBuilder.categoryId]);

  const handleAddonQtyChange = (id, rawValue) => {
    const value = Number(rawValue);
    setAddonSelection((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });
  };

  const handleAddCatalogLine = () => {
    if (!catalogBuilder.productId) {
      setBuilderError('Selecciona un modelo del catálogo.');
      return;
    }
    const width = Number(catalogBuilder.width);
    const height = Number(catalogBuilder.height);
    const quantity = Number(catalogBuilder.quantity) || 1;
    const autoPriceInfo = width && height ? getWindowBasePrice(width, height, catalogBuilder.series) : null;
    const unitPrice = autoPriceInfo?.price || 0;
    const selectedDesign = designCatalog.find((design) => design.id === catalogBuilder.productId);
    const colorLabel = colorOptions.find((opt) => opt.id === catalogBuilder.color)?.label;
    const glassLabel = glassOptions.find((opt) => opt.id === catalogBuilder.glass)?.label;
    const finishLabel = finishOptions.find((opt) => opt.id === catalogBuilder.finish)?.label;

    const descriptionParts = [
      width && height ? `${width}" x ${height}"` : null,
      catalogBuilder.series ? `Serie ${catalogBuilder.series.toUpperCase()}` : null,
      colorLabel,
      glassLabel,
      finishLabel,
    ].filter(Boolean);

    if (autoPriceInfo?.widthExceeded) {
      descriptionParts.push('Ancho supera la tabla (precio ajustado a 36")');
    }

    const baseItem = {
      name: `${selectedDesign?.title || 'Modelo'} · ${
        selectedDesign?.categoryName || productCategories.find((c) => c.id === catalogBuilder.categoryId)?.title || ''
      }`,
      description: descriptionParts.join(' · ') || 'Cotización desde catálogo',
      quantity,
      price: unitPrice,
      catalogMeta: {
        productId: catalogBuilder.productId,
        categoryId: catalogBuilder.categoryId,
        width,
        height,
        series: catalogBuilder.series,
        color: catalogBuilder.color,
        glass: catalogBuilder.glass,
        finish: catalogBuilder.finish,
        autoPriceInfo,
      },
    };

    const entries = [baseItem];
    Object.entries(addonSelection).forEach(([accessoryId, qtyValue]) => {
      const qty = Number(qtyValue);
      if (!qty) return;
      const info = accessoryOptions.find((acc) => acc.id === accessoryId);
      if (!info) return;
      entries.push({
        name: `Accesorio · ${info.name}`,
        description: info.description || '',
        quantity: qty,
        price: Number(info.price) || 0,
        catalogMeta: { accessoryId },
      });
    });
    setItemList((prev) => [...prev, ...entries]);

    setCatalogBuilder((prev) => ({ ...prev, width: '', height: '', quantity: 1 }));
    setAddonSelection({});
    setBuilderError('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Propuestas enviadas</h2>
        <p className="text-gray-500">Genera estimados inteligentes y dale seguimiento a tus clientes.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            resetBuilder();
            setBuilderOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
        >
          Crear propuesta
        </button>
        <p className="text-xs uppercase tracking-[0.3em] text-gray-400">
          {loading ? 'Cargando…' : `${proposals.length} propuestas`}
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Cargando propuestas…</p>
      ) : proposals.length === 0 ? (
        <p className="text-sm text-gray-500">Aún no tienes propuestas registradas.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {proposals.map((proposal) => (
            <article
              key={proposal.id}
              className="rounded-2xl border border-orange-100 bg-white shadow-sm"
            >
              <div className="border-b border-orange-50 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.35em] text-orange-500">
                  {proposal.status || 'pendiente'}
                </p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {proposal.clientInfo?.name || proposal.clientInfo?.email || 'Cliente sin nombre'}
                </h3>
                <p className="text-xs text-gray-500">
                  {proposal.createdAt?.toDate?.().toLocaleDateString?.('es-PR') || 'Fecha no disponible'}
                </p>
              </div>
              <div className="px-4 py-3 text-sm text-gray-700">
                <p>
                  Total estimado:{' '}
                  <span className="font-semibold">
                    ${Number(proposal.total || 0).toFixed(2)}
                  </span>
                </p>
                <p>
                  Modelos cotizados:{' '}
                  {(proposal.items || []).length || (proposal.windowModels || []).length || 0}
                </p>
                <p className="text-xs text-gray-500">
                  Token: {proposal.publicToken?.slice?.(0, 8) || proposal.id.slice(0, 8)}…
                </p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setProposalPdfId(proposal.id);
                      downloadProposalPdf(proposal)
                        .catch((err) => console.error('❌ Error descargando propuesta:', err))
                        .finally(() => setProposalPdfId(''));
                    }}
                    className="rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:bg-gray-100 disabled:cursor-wait disabled:opacity-60"
                    disabled={proposalPdfId === proposal.id}
                  >
                    {proposalPdfId === proposal.id ? 'Descargando…' : 'Descargar PDF'}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setProposalEmailId(proposal.id);
                      try {
                        await sendProposalEmailToClient(proposal, { silent: false });
                      } finally {
                        setProposalEmailId('');
                      }
                    }}
                    className="rounded-full border border-emerald-200 px-3 py-1 text-emerald-700 hover:bg-emerald-50 disabled:cursor-wait disabled:opacity-60"
                    disabled={proposalEmailId === proposal.id}
                  >
                    {proposalEmailId === proposal.id ? 'Enviando…' : 'Enviar por email'}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {builderOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-gray-400">Nueva propuesta</p>
                <h3 className="text-2xl font-semibold text-gray-900">Construye el estimado</h3>
                <p className="text-sm text-gray-500">
                  Usa tus clientes asignados o ingresa uno nuevo. El sistema enviará el PDF al correo indicado.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setBuilderOpen(false);
                  resetBuilder();
                }}
                className="text-sm font-semibold text-gray-400 hover:text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <form className="mt-6 space-y-6" onSubmit={handleSubmitProposal}>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Cliente existente</label>
                  <select
                    value={clientForm.clientId}
                    onChange={(e) => handleSelectClient(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="">— Seleccionar —</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {(client.businessName || client.name || 'Cliente sin nombre').slice(0, 40)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Depósito (opcional)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={deposit}
                    onChange={(e) => setDeposit(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    placeholder="Ej. 2500"
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Nombre del cliente</label>
                  <input
                    type="text"
                    value={clientForm.name}
                    onChange={(e) => setClientForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    placeholder="Ej. Condominio Brisas"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Correo</label>
                  <input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    placeholder="cliente@correo.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Teléfono</label>
                  <input
                    type="tel"
                    value={clientForm.phone}
                    onChange={(e) => setClientForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    placeholder="787-000-0000"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Ubicación / proyecto</label>
                  <input
                    type="text"
                    value={clientForm.projectLocation}
                    onChange={(e) =>
                      setClientForm((prev) => ({ ...prev, projectLocation: e.target.value }))
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    placeholder="Ej. Guaynabo, Residencial"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Modelos y servicios
                  </label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-sm font-semibold text-emerald-600 hover:text-emerald-800"
                  >
                    + Añadir línea
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  {itemList.map((item, index) => (
                    <div
                      key={`item-${index}`}
                      className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3"
                    >
                      <div className="grid gap-2 md:grid-cols-2">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Descripción</label>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                            placeholder="Ventana casement · Impact"
                            required={index === 0}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Notas</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                            placeholder={'72" x 48" · Lama 3 · Clear'}
                          />
                        </div>
                      </div>
                      <div className="mt-2 grid gap-2 md:grid-cols-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Cantidad</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Precio unitario</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                        <div className="flex items-end justify-end">
                          {itemList.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-xs font-semibold text-red-500 hover:text-red-700"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              <p className="mt-3 text-sm font-semibold text-gray-900">
                Total estimado:{' '}
                <span className="text-emerald-700">
                  ${total.toLocaleString('es-PR', { minimumFractionDigits: 2 })}
                </span>
                {depositNumber > 0 && (
                  <>
                    {' '}
                    · Depósito ${depositNumber.toLocaleString('es-PR', { minimumFractionDigits: 2 })}
                  </>
                )}
              </p>
            </div>

            <div className="space-y-6 rounded-3xl border border-gray-200 bg-white/70 px-4 py-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Categoría</label>
                  <select
                    value={catalogBuilder.categoryId}
                    onChange={(e) =>
                      setCatalogBuilder((prev) => ({
                        ...prev,
                        categoryId: e.target.value,
                        productId:
                          designCatalog.find((design) => design.categoryId === e.target.value)?.id || '',
                      }))
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {productCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-600">Modelo</label>
                  <select
                    value={catalogBuilder.productId}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, productId: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {availableDesigns.map((design) => (
                      <option key={design.id} value={design.id}>
                        {design.title} · {design.desc}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Ancho (")</label>
                  <input
                    type="number"
                    min="1"
                    value={catalogBuilder.width}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, width: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Alto (")</label>
                  <input
                    type="number"
                    min="1"
                    value={catalogBuilder.height}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, height: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Serie (lama)</label>
                  <select
                    value={catalogBuilder.series}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, series: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="lama3">Lama 3</option>
                    <option value="lama4">Lama 4</option>
                    <option value="lama6">Lama 6</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={catalogBuilder.quantity}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, quantity: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Color</label>
                  <select
                    value={catalogBuilder.color}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, color: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {colorOptions.map((color) => (
                      <option key={color.id} value={color.id}>
                        {color.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Cristal</label>
                  <select
                    value={catalogBuilder.glass}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, glass: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {glassOptions.map((glass) => (
                      <option key={glass.id} value={glass.id}>
                        {glass.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Acabado</label>
                  <select
                    value={catalogBuilder.finish}
                    onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, finish: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {finishOptions.map((finish) => (
                      <option key={finish.id} value={finish.id}>
                        {finish.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Accesorios rápidos</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {filteredAccessories.map((addon) => (
                    <label
                      key={addon.id}
                      className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600"
                    >
                      <span>
                        {addon.name}{' '}
                        <span className="text-[11px] text-gray-400">(${Number(addon.price || 0).toFixed(2)})</span>
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={addonSelection[addon.id] || ''}
                        onChange={(e) => handleAddonQtyChange(addon.id, e.target.value)}
                        className="ml-2 w-16 rounded-xl border border-gray-200 px-2 py-1 text-right text-xs text-gray-700 focus:border-emerald-500 focus:outline-none"
                        placeholder="Qty"
                      />
                    </label>
                  ))}
                </div>
              </div>
              {catalogPricePreview && (
                <p className="rounded-2xl bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                  Precio sugerido (serie {catalogBuilder.series.toUpperCase()}): $
                  {Number(catalogPricePreview.price || 0).toFixed(2)}{' '}
                  {catalogPricePreview.widthExceeded && (
                    <span className="text-orange-600">
                      · Ancho excede 36", se usó tarifa máxima hasta confirmar medidas.
                    </span>
                  )}
                </p>
              )}
              <button
                type="button"
                onClick={handleAddCatalogLine}
                className="w-full rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Agregar desde catálogo
              </button>
            </div>

            {builderError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                {builderError}
              </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500">
                  El cliente recibirá un PDF con los modelos seleccionados y se registrará en tu historial.
                </p>
                <button
                  type="submit"
                  disabled={builderSubmitting}
                  className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
                >
                  {builderSubmitting ? 'Enviando…' : 'Enviar propuesta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerProposals;
